/**
 * API 客户端封装
 *
 * 基于 fetch 封装，提供以下能力：
 *  - 自动从 localStorage 读取 JWT Token 并附加 Authorization: Bearer 头
 *  - 统一处理 JSON 请求/响应
 *  - 401 响应自动清除 Token 并重定向到登录页
 *  - 统一错误抛出格式 { success, error }
 *  - 支持 GET / POST / PUT / PATCH / DELETE 方法
 */

import type { ApiResponse } from '@/types';

// 后端 API 基础路径（开发环境通过 Vite proxy 转发，生产环境可由网关处理）
const API_BASE_URL = '/api/v1';

// Token 在 localStorage 中的存储键
const TOKEN_KEY = 'qms_token';

/**
 * 获取当前存储的 Token
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 设置 Token
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * 清除 Token
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * 统一处理 401 未授权：清除 Token 并跳转登录页
 * 使用 location 跳转以确保完全重置应用状态
 */
function handleUnauthorized(): void {
  clearToken();
  // 避免在登录页重复跳转
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

/**
 * 构建请求头，自动附加 Authorization
 */
function buildHeaders(customHeaders?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((customHeaders as Record<string, string>) ?? {}),
  };

  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * 统一响应解析
 * 后端返回格式: { success: boolean, data?: T, error?: string }
 */
async function parseResponse<T>(response: Response): Promise<T> {
  // 401 未授权：Token 过期或无效
  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('登录已过期，请重新登录', 401);
  }

  // 尝试解析 JSON 响应体
  let body: ApiResponse<T> | null = null;
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    body = (await response.json()) as ApiResponse<T>;
  }

  // HTTP 状态码非 2xx 视为错误
  if (!response.ok) {
    const message = body?.error ?? body?.message ?? `请求失败 (${response.status})`;
    throw new ApiError(message, response.status);
  }

  // 业务层面的失败（success: false）
  if (body && body.success === false) {
    throw new ApiError(body.error ?? body.message ?? '操作失败', response.status);
  }

  // 返回 data 字段（符合 ApiResponse 结构约定）
  return (body?.data ?? (body as unknown as T)) as T;
}

/**
 * 自定义 API 错误类
 */
export class ApiError extends Error {
  statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

/**
 * 构建完整 URL，支持 query 参数
 */
function buildUrl(path: string, params?: Record<string, unknown>): string {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

  if (!params) return url;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * 通用请求方法
 */
async function request<T>(
  method: string,
  path: string,
  options?: {
    params?: Record<string, unknown>;
    body?: unknown;
    headers?: HeadersInit;
    signal?: AbortSignal;
  },
): Promise<T> {
  const url = buildUrl(path, options?.params);

  const response = await fetch(url, {
    method,
    headers: buildHeaders(options?.headers),
    body: options?.body ? JSON.stringify(options.body) : undefined,
    signal: options?.signal,
  });

  return parseResponse<T>(response);
}

/**
 * API 客户端
 * 提供 GET / POST / PUT / PATCH / DELETE 方法
 */
export const apiClient = {
  get<T>(path: string, params?: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
    return request<T>('GET', path, { params, signal });
  },

  post<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
    return request<T>('POST', path, { body, signal });
  },

  postForm<T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> {
    const headers: Record<string, string> = {};
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(buildUrl(path), { method: 'POST', headers, body: form, signal }).then((response) => parseResponse<T>(response));
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('PUT', path, { body });
  },

  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('PATCH', path, { body });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>('DELETE', path);
  },
};
